import { watch, computed, reactive, ref } from 'vue'
import { NuxeoOrdersRequest } from '@/contracts/api/RequestContracts'
import { NuxeoPageProviderResponse, HMCTSOrderState } from '@/contracts/api/ResponseContracts'
import { axiosPostOrders } from '@/composables/api'
import { FieldsStructure } from '@/contracts/forms/FieldContracts'
import { pageprovider } from '@/composables/pageprovider'
import { useStore } from 'vuex'

// Prepare orders methods and data:
export function orders(pageSize = 10)
{
    // Use vuex:
    const store = useStore()
    // Get user data:
    const authenticatedUser = computed(() =>
    {
        return store.getters['auth/getUser']
    })
    // Prepare for page provider:
    const {
        data,
        numberOfPaginationPages,
        currentPageIndex,
        sortDirection,
        sortColumn,
        prevPage,
        nextPage,
        goToPage
    } = pageprovider()
    // Hold reactive form data:
    const filterFormData = reactive({} as FieldsStructure)
    // Set order params for request. Deliberately no customerID: the proxy's
    // /orders route derives it from the verified Bearer token (register Q34),
    // so a client-supplied value would never be trusted anyway.
    const orderParams = computed(() =>
    {
        return {
            currentPageIndex: currentPageIndex.value,
            sortBy: (sortColumn.value === 'none' ? '' : sortColumn.value),
            sortOrder: (sortDirection.value === 'ascending' ? 'ASC' : 'DESC'),
            pageSize: pageSize,
            orderNumber: (filterFormData.ordernumber !== undefined && typeof filterFormData.ordernumber.value === 'string' ? filterFormData.ordernumber.value.toUpperCase() : ''),
            surname: (filterFormData.surname !== undefined && typeof filterFormData.surname.value === 'string' ? filterFormData.surname.value.toUpperCase() : ''),
            firstnames: (filterFormData.firstnames !== undefined && typeof filterFormData.firstnames.value === 'string' ? filterFormData.firstnames.value.toUpperCase() : '')
        } as NuxeoOrdersRequest
    })
    // How many grants the customer has paid for, from the operation's paging
    // metadata rather than the page length: IRM's reworked GetAllOrderedGrants
    // (2026-08-24) returns a PaginableDocumentType whose resultsCount is the
    // full match count whatever the pageSize (verified vs dit02 + stg01
    // 2026-09-22: pageSize 1 → resultsCount 8 / 5). So a caller that only needs
    // the count (the Header badge) can ask for a single-record page instead of
    // pulling every grant with every order ever placed on it. Falls back to the
    // page length if the envelope ever loses resultsCount (the pre-rework op
    // returned a plain, unpaged list — the length WAS the count).
    const ordersCount = computed(() =>
    {
        if(typeof data.resultsCount === 'number' && data.resultsCount >= 0)
        {
            return data.resultsCount
        }
        return Array.isArray(data.entries) ? data.entries.length : 0
    })
    // Function to set filtering data:
    const setFilterParams = function(filterData: FieldsStructure)
    {
        Object.assign(filterFormData, filterData)
    }
    // Watch to trigger search:
    watch(filterFormData, function()
    {
        currentPageIndex.value = 0
        getOrders()
    })
    // Watch to trigger search:
    watch(currentPageIndex, function()
    {
        getOrders()
    })
    watch(authenticatedUser, function()
    {
        getOrders()
    })
    // Refetch when a payment is confirmed (PayResult commits
    // orders/markChanged): the Header's snapshot is read at boot — on the
    // return from SmartPay that is BEFORE the confirm — and nothing else
    // refreshes it. One extra read per confirmed payment; getOrders() keeps
    // its signed-in gate, and this never fires on route changes.
    watch(() => store.getters['orders/getRevision'], function()
    {
        getOrders()
    })
    // Filter order state function:
    const filterOrderState = function(orderStates: Array<HMCTSOrderState>): Array<HMCTSOrderState>
    {
        const orderNumbers: Array<string> = []
        const ordersStatesCopy: Array<HMCTSOrderState> = []
        for(let i = 0; i < orderStates.length; i++)
        {
            if(!orderNumbers.includes(orderStates[i].orderNumber))
            {
                ordersStatesCopy.push(orderStates[i])
                orderNumbers.push(orderStates[i].orderNumber)
            }
        }
        return ordersStatesCopy
    }
    // Sort order state function:
    const compareOrderState = function(a: HMCTSOrderState, b: HMCTSOrderState)
    {
        if(a.orderNumber === b.orderNumber)
        {
            return -1
        }
        if(new Date(a.orderDate).getTime() < new Date(b.orderDate).getTime())
        {
            return 1
        }
        if(new Date(a.orderDate).getTime() > new Date(b.orderDate).getTime())
        {
            return -1
        }
        return 0
    }
    // Get orders:
    const getOrders = function()
    {
        // Only fetch when authenticated. The proxy's /orders route is
        // fail-closed (401 without a Bearer), so an anonymous call — Header on
        // the landing page, or the authenticatedUser watcher on logout — would
        // just be a wasted round-trip that IRM sees as a signed-out orders
        // read (their 2026-07-15 report).
        if(!store.getters['auth/isAuthenticated'])
        {
            return
        }
        axiosPostOrders(
        {
            params: orderParams.value
        },
        function(response: NuxeoPageProviderResponse)
        {
            // Order by most recent:
            for(let i = 0; i < response.entries.length; i++)
            {
                const orderState = filterOrderState(response.entries[i].properties['hmctsgrant:ordersState'])
                orderState.sort(compareOrderState)
                response.entries[i].properties['hmctsgrant:ordersState'] = orderState
            }
            Object.assign(data, response)
        })
    }

    getOrders()

    return {
        data,
        numberOfPaginationPages,
        currentPageIndex,
        sortDirection,
        sortColumn,
        getOrders,
        prevPage,
        nextPage,
        goToPage,
        setFilterParams,
        ordersCount
    }
}
